// src/components/MarcaUI.tsx
// Piezas del diseño "marca" (Marca.css), compartidas por el asistente de
// primeros pasos, Horarios y Servicios: así las tres pantallas se ven iguales
// y un arreglo en una vale para las otras.
import React from 'react';
import { ChevronLeft, Copy, Plus, X } from 'lucide-react';
import './Marca.css';

// ===== Interruptor =====

interface InterruptorProps {
  activo: boolean;
  onCambio: (valor: boolean) => void;
  /** Para lectores de pantalla: qué se prende y se apaga */
  etiqueta: string;
  deshabilitado?: boolean;
}

export const Interruptor: React.FC<InterruptorProps> = ({ activo, onCambio, etiqueta, deshabilitado }) => (
  <button
    type="button"
    role="switch"
    aria-checked={activo}
    aria-label={etiqueta}
    disabled={deshabilitado}
    onClick={() => onCambio(!activo)}
    className="marca-switch relative w-10 h-[22px] rounded-full shrink-0"
  >
    <span className="absolute top-[3px] left-[3px] w-4 h-4 rounded-full bg-white shadow" />
  </button>
);

// ===== Una opción con su interruptor (título, detalle y lo que se abre) =====

interface OpcionConInterruptorProps {
  titulo: string;
  detalle?: React.ReactNode;
  activo: boolean;
  onCambio: (valor: boolean) => void;
  deshabilitado?: boolean;
  /** Lo que aparece abajo cuando está prendida */
  children?: React.ReactNode;
}

export const OpcionConInterruptor: React.FC<OpcionConInterruptorProps> = ({ titulo, detalle, activo, onCambio, deshabilitado, children }) => (
  <div>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">{titulo}</p>
        {detalle && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{detalle}</p>}
      </div>
      <Interruptor activo={activo} onCambio={onCambio} etiqueta={titulo} deshabilitado={deshabilitado} />
    </div>
    {activo && children && <div className="mt-3">{children}</div>}
  </div>
);

// ===== Fichas (opciones de a una) =====

interface GrupoDeFichasProps<T extends string | number> {
  opciones: [T, string][];
  valor: T;
  onCambio: (valor: T) => void;
  etiqueta?: string;
  chicas?: boolean;
}

export function GrupoDeFichas<T extends string | number>({ opciones, valor, onCambio, etiqueta, chicas }: GrupoDeFichasProps<T>) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={etiqueta}>
      {opciones.map(([v, texto]) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={valor === v}
          onClick={() => onCambio(v)}
          className={`marca-ficha rounded-xl transition-colors ${chicas ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
        >
          {texto}
        </button>
      ))}
    </div>
  );
}

// ===== Un día de la semana =====

export interface DiaEditable {
  nombre: string;
  atiende: boolean;
  desde: string;
  hasta: string;
  corte: boolean;
  corteDesde: string;
  corteHasta: string;
}

const minutos = (hora: string) => {
  const [h, m] = (hora || '00:00').split(':').map(Number);
  return h * 60 + m;
};

export const errorDelDia = (d: DiaEditable): string | null => {
  if (!d.atiende) return null;
  if (minutos(d.hasta) <= minutos(d.desde)) return 'La hora de cierre tiene que ser después de la de apertura.';
  if (d.corte) {
    if (minutos(d.corteHasta) <= minutos(d.corteDesde)) return 'El corte tiene que terminar después de empezar.';
    if (minutos(d.corteDesde) <= minutos(d.desde) || minutos(d.corteHasta) >= minutos(d.hasta)) {
      return 'El corte tiene que quedar dentro del horario.';
    }
  }
  return null;
};

/** Horas de `desde` a `hasta` cada `paso` minutos ("06:00"...) */
export const listaDeHoras = (desde = 6 * 60, hasta = 23 * 60 + 55, paso = 5): string[] => {
  const lista: string[] = [];
  for (let m = desde; m <= hasta; m += paso) {
    lista.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return lista;
};

interface SelectorDeHoraProps {
  valor: string;
  horas: string[];
  onCambio: (hora: string) => void;
  etiqueta: string;
}

// Si la hora guardada no está en la lista (otro paso de minutos), se suma:
// si no, el desplegable mostraría la primera opción y la guardaría así.
export const SelectorDeHora: React.FC<SelectorDeHoraProps> = ({ valor, horas, onCambio, etiqueta }) => {
  const opciones = valor && !horas.includes(valor) ? [...horas, valor].sort() : horas;
  return (
    <select
      aria-label={etiqueta}
      value={valor}
      onChange={(e) => onCambio(e.target.value)}
      className="marca-campo rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
    >
      {opciones.map((h) => <option key={h} value={h}>{h}</option>)}
    </select>
  );
};

interface FilaDeDiaProps {
  dia: DiaEditable;
  horas: string[];
  onCambio: (cambios: Partial<DiaEditable>) => void;
  /** Qué está mal en este día (se muestra abajo, en rojo) */
  problema?: string | null;
  /** Si viene, la fila ofrece copiar su horario a los demás días */
  onCopiarATodos?: () => void;
  /** Texto para el día apagado (por defecto "No atiendo") */
  textoApagado?: string;
}

export const FilaDeDia: React.FC<FilaDeDiaProps> = ({ dia: d, horas, onCambio, problema, onCopiarATodos, textoApagado = 'No atiendo' }) => (
  <div className={`rounded-2xl border p-3 transition-colors ${d.atiende ? 'marca-tarjeta' : 'bg-gray-50 border-gray-200'}`}>
    <div className="flex items-center gap-3">
      <Interruptor activo={d.atiende} onCambio={(v) => onCambio({ atiende: v })} etiqueta={`Atiendo los ${d.nombre.toLowerCase()}`} />
      <span className={`flex-1 font-semibold ${d.atiende ? 'text-gray-900' : 'text-gray-400'}`}>{d.nombre}</span>
      {!d.atiende && <span className="text-xs text-gray-400">{textoApagado}</span>}
      {d.atiende && onCopiarATodos && (
        <button
          type="button"
          onClick={onCopiarATodos}
          className="marca-acento inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg hover:bg-white/70"
          title={`Usar el horario del ${d.nombre.toLowerCase()} en todos los días`}
        >
          <Copy className="w-3.5 h-3.5" /> Copiar a todos
        </button>
      )}
    </div>

    {d.atiende && (
      <div className="mt-2.5 pl-[52px] space-y-2">
        <div className="flex items-center gap-2">
          <SelectorDeHora etiqueta={`${d.nombre}: desde`} valor={d.desde} horas={horas} onCambio={(h) => onCambio({ desde: h })} />
          <span className="text-sm text-gray-500">a</span>
          <SelectorDeHora etiqueta={`${d.nombre}: hasta`} valor={d.hasta} horas={horas} onCambio={(h) => onCambio({ hasta: h })} />
        </div>
        {d.corte ? (
          // gap-x de 6 px: con 8 la X se iba al renglón de abajo en la
          // pantalla de Horarios del celular (tiene más margen que el asistente)
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
            <span className="text-xs font-medium text-gray-600">Corte</span>
            <SelectorDeHora etiqueta={`${d.nombre}: corte desde`} valor={d.corteDesde} horas={horas} onCambio={(h) => onCambio({ corteDesde: h })} />
            <span className="text-sm text-gray-500">a</span>
            <SelectorDeHora etiqueta={`${d.nombre}: corte hasta`} valor={d.corteHasta} horas={horas} onCambio={(h) => onCambio({ corteHasta: h })} />
            <button
              type="button"
              onClick={() => onCambio({ corte: false })}
              className="p-1 text-gray-400 hover:text-gray-600"
              aria-label={`Sacar el corte del ${d.nombre.toLowerCase()}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onCambio({ corte: true })}
            className="marca-acento inline-flex items-center gap-1 text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar un corte (ej: al mediodía)
          </button>
        )}
        {problema && <p className="text-xs text-red-600">{problema}</p>}
      </div>
    )}
  </div>
);

// ===== Portada de una pantalla =====

interface PortadaMarcaProps {
  titulo: string;
  bajada?: React.ReactNode;
  onVolver?: () => void;
  /** Botón de la derecha (ej: "Nuevo servicio") */
  accion?: React.ReactNode;
  /** Debajo del título (ej: un resumen) */
  children?: React.ReactNode;
  /** Ancho del contenido, igual que el de la pantalla */
  ancho?: string;
}

export const PortadaMarca: React.FC<PortadaMarcaProps> = ({ titulo, bajada, onVolver, accion, children, ancho = 'max-w-3xl' }) => (
  <div className="marca-portada marca-portada--pagina relative overflow-hidden text-white px-4 sm:px-6 pt-3 pb-12 text-left">
    <span className="marca-mancha marca-mancha--1" />
    <span className="marca-mancha marca-mancha--2" />
    <div className={`relative ${ancho} mx-auto`}>
      <div className="flex items-center justify-between gap-3 min-h-[40px]">
        {onVolver ? (
          <button
            type="button"
            onClick={onVolver}
            aria-label="Volver"
            className="-ml-2 p-2 rounded-xl hover:bg-white/15 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : <span />}
        {accion}
      </div>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold leading-tight">{titulo}</h1>
      {bajada && <p className="mt-1 text-sm text-white/90 leading-snug">{bajada}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  </div>
);
